import React from 'react';

import { Input } from '@mantine/core';
import { useShallowEffect } from '@mantine/hooks';
import { Link, RichTextEditor } from '@mantine/tiptap';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import RichTextBoldControl from 'components/RichTextEditor/Controls/RichTextBoldControl';
import RichTextLinkControl from 'components/RichTextEditor/Controls/RichTextLinkControl';
import DOMPurify from 'dompurify';
import { normalizeToHtml } from 'utils/html';

// Mirrors the backend allowlist in kilo_models/utils/sanitize_html.py so the editor only keeps
// what will survive server-side sanitization — pasted content that the backend would strip is
// removed on change instead of rendering and then silently vanishing after save.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 'a', 'ul', 'ol', 'li', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
};

interface WorkoutRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: React.ReactNode;
  editable?: boolean;
  error?: React.ReactNode;
  mih?: number;
  dataCy?: string;
}

/**
 * Shared rich-text editor for the two athlete-facing workout fields ("Describe the Workout" per
 * section and global "Athlete Notes"). Toolbar is fixed to the KILO-565 spec actions: bold/italic/
 * underline, insert/remove link, align left/center/right, ordered/unordered list. HTML is sanitized
 * with DOMPurify on every change; links carry hardened attributes. Plain-text values are
 * normalized to HTML on load so their line breaks render.
 */
const WorkoutRichTextEditor = ({
  value,
  onChange,
  label,
  editable = true,
  error,
  mih = 120,
  dataCy,
}: WorkoutRichTextEditorProps): JSX.Element => {
  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline as AnyExtension,
      TextAlign.configure({ types: ['paragraph'] }) as AnyExtension,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }) as AnyExtension,
    ],
    content: normalizeToHtml(value),
    editable,
    onUpdate: ({ editor: _editor }) => onChange(DOMPurify.sanitize(_editor.getHTML(), SANITIZE_CONFIG)),
  });

  // Sync external value (e.g. form data loaded async on edit pages) without emitting an update,
  // which would loop back through onChange. The equality guard avoids resetting the cursor while typing.
  useShallowEffect(() => {
    const html = normalizeToHtml(value);
    if (editor && html !== editor.getHTML()) {
      editor.commands.setContent(html, false);
    }
  }, [editor, value]);

  useShallowEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  const content = (
    <RichTextEditor editor={editor}>
      {editable && (
        <RichTextEditor.Toolbar sticky>
          <RichTextEditor.ControlsGroup>
            <RichTextBoldControl />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextLinkControl />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.AlignLeft />
            <RichTextEditor.AlignCenter />
            <RichTextEditor.AlignRight />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
      )}
      <RichTextEditor.Content mih={mih} data-cy={dataCy} />
    </RichTextEditor>
  );

  if (label || error) {
    return (
      <Input.Wrapper label={label} error={error}>
        {content}
      </Input.Wrapper>
    );
  }

  return content;
};

export default WorkoutRichTextEditor;
